import React, { useState, useRef, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faSearch } from "@fortawesome/free-solid-svg-icons";
import DSCheckbox from "./Checkbox";
import usePopoverPlacement from "./usePopoverPlacement";

/**
 * DS Dropdown — Figma "dropdown" (291:1308) 컴포넌트.
 * 클릭 시 popover로 옵션 리스트 노출.
 *
 * 두 가지 mode:
 *  - normal (default): 단일 선택. value=string, onChange(value).
 *  - multi (figma 291:1305 — property2="checkbox"): 다중 선택. value=array, onChange(array).
 *    multi 모드에서는 popover 내부에 검색 input + "전체" 체크박스 + "N개 선택 / M개" 카운터 노출.
 *
 * @param {object} props
 * @param {Array<{value: string|number, text: string, content?: React.ReactNode, disabled?: boolean}>} props.options
 * @param {string|number|Array<string|number>} [props.value]
 * @param {(value: string|number|Array) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {string} [props.className]
 * @param {React.CSSProperties} [props.style]
 * @param {boolean} [props.disabled]
 * @param {number} [props.minPopoverWidth]
 * @param {"xs"|"md"} [props.size="md"]
 * @param {boolean} [props.multi=false] - multi-select with checkbox + search (figma 291:1305)
 * @param {boolean} [props.multiline=false] - 긴 라벨을 1줄 ellipsis 대신 최대 2줄로 접는다
 * @param {string} [props.searchPlaceholder="검색어를 입력해 주세요"]
 * @param {(count: number) => string} [props.multiLabelFormatter] - 선택된 항목 라벨 포맷 (multi 모드)
 */
const DSDropdown = ({
  options = [],
  value,
  onChange,
  placeholder,
  className = "",
  style,
  disabled = false,
  minPopoverWidth,
  size = "md",
  multi = false,
  multiline = false,
  searchable = false,
  searchPlaceholder = "검색어를 입력해 주세요",
  multiLabelFormatter,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  // 팝오버 높이·방향을 뷰포트에 맞춘다 (DSTimeInput 과 공용 훅)
  const popoverStyle = usePopoverPlacement(open, wrapperRef);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  // 닫힐 때 search 초기화
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // --- 단일 선택 모드 ---
  const selected = !multi && options.find((opt) => opt.value === value);
  const labelText = selected ? selected.text : placeholder || "";
  const isPlaceholder = multi
    ? !value || (Array.isArray(value) && value.length === 0)
    : !selected && !!placeholder;

  // --- multi 모드 ---
  const selectedSet = useMemo(
    () => new Set(Array.isArray(value) ? value : []),
    [value],
  );
  const multiLabel = useMemo(() => {
    if (!multi) return labelText;
    const count = selectedSet.size;
    if (count === 0) return placeholder || "";
    if (multiLabelFormatter) return multiLabelFormatter(count);
    return `${count}개 선택`;
  }, [multi, selectedSet, placeholder, multiLabelFormatter, labelText]);

  const filteredOptions = useMemo(() => {
    if (!multi || !search.trim()) return options;
    const lower = search.trim().toLowerCase();
    return options.filter((o) =>
      (o.text || "").toString().toLowerCase().includes(lower),
    );
  }, [multi, options, search]);

  const handleSelect = (optValue) => {
    if (!multi) {
      if (onChange) onChange(optValue);
      setOpen(false);
      return;
    }
    // multi: toggle
    if (!onChange) return;
    const next = new Set(selectedSet);
    if (next.has(optValue)) next.delete(optValue);
    else next.add(optValue);
    onChange(Array.from(next));
  };

  const isAllSelected =
    multi && options.length > 0 && options.every((o) => selectedSet.has(o.value));
  const isPartialSelected =
    multi && !isAllSelected && options.some((o) => selectedSet.has(o.value));

  const handleSelectAll = (checked) => {
    if (!onChange) return;
    onChange(checked ? options.map((o) => o.value) : []);
  };

  return (
    <div className={`ds-dropdown-wrapper ${className}`} ref={wrapperRef} style={style}>
      <button
        type="button"
        disabled={disabled}
        className={`ds-dropdown ${size === "xs" ? "ds-dropdown--xs" : ""} ${
          isPlaceholder ? "ds-dropdown--placeholder" : ""
        } ${multiline ? "ds-dropdown--multiline" : ""} ${
          open ? "ds-dropdown--open" : ""
        }`}
        onClick={() => !disabled && setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ds-dropdown__label">{multi ? multiLabel : labelText}</span>
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className="ds-dropdown__chevron"
        />
      </button>
      {open && !multi && (
        <div
          className="ds-dropdown-popover"
          role="listbox"
          style={{ ...(minPopoverWidth ? { minWidth: minPopoverWidth } : {}), ...(popoverStyle || {}) }}
        >
          {searchable && (
            <div className="ds-dropdown-popover__search">
              <FontAwesomeIcon icon={faSearch} className="ds-dropdown-popover__search-icon" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="ds-dropdown-popover__search-input"
              />
            </div>
          )}
          {(searchable && search.trim()
            ? options.filter((o) =>
                (o.text || "").toString().toLowerCase().includes(search.trim().toLowerCase()),
              )
            : options
          ).map((opt) => {
            const isSelected = opt.value === value;
            const isDisabled = !!opt.disabled;
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={isSelected}
                aria-disabled={isDisabled}
                disabled={isDisabled}
                className={`ds-dropdown-popover__item ${
                  isSelected ? "ds-dropdown-popover__item--selected" : ""
                } ${isDisabled ? "ds-dropdown-popover__item--disabled" : ""}`}
                onClick={() => !isDisabled && handleSelect(opt.value)}
              >
                {opt.content || opt.text}
              </button>
            );
          })}
        </div>
      )}
      {/* multi mode popover (figma 291:1305) — search + 전체 + 카운터 + checkbox 리스트 */}
      {open && multi && (
        <div
          className="ds-dropdown-popover ds-dropdown-popover--multi"
          role="listbox"
          aria-multiselectable="true"
          style={{ ...(minPopoverWidth ? { minWidth: minPopoverWidth } : {}), ...(popoverStyle || {}) }}
        >
          {/* 검색 */}
          <div className="ds-dropdown-popover__search">
            <FontAwesomeIcon icon={faSearch} className="ds-dropdown-popover__search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="ds-dropdown-popover__search-input"
            />
          </div>
          {/* 전체 + 카운터 */}
          <div className="ds-dropdown-popover__head">
            <DSCheckbox
              checked={isAllSelected}
              indeterminate={isPartialSelected}
              onChange={handleSelectAll}
              label="전체"
            />
            <span className="ds-dropdown-popover__count">
              {selectedSet.size}개 선택{" "}
              <span className="ds-dropdown-popover__count-total">/ {options.length}개</span>
            </span>
          </div>
          {/* 옵션 리스트 */}
          <div className="ds-dropdown-popover__list">
            {filteredOptions.length === 0 ? (
              <div className="ds-dropdown-popover__empty">검색 결과가 없습니다.</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedSet.has(opt.value);
                // 외부 div 의 onClick + 내부 DSCheckbox 의 onChange 둘 다 걸면
                // 라벨 클릭 시 같은 클릭이 toggle 2회로 처리되어 무변화 발생.
                // → div 는 시각 컨테이너만 담당 (clickable label 은 DSCheckbox 내부 <label> 이 처리).
                return (
                  <div
                    key={String(opt.value)}
                    className="ds-dropdown-popover__multi-item"
                  >
                    <DSCheckbox
                      checked={isSelected}
                      onChange={() => handleSelect(opt.value)}
                      label={opt.text}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(DSDropdown);
